import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dependentRequestsAPI } from '../../services/api';

function DependentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [error, setError] = useState('');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await dependentRequestsAPI.getAll({ status: statusFilter });
      if (response.success) {
        setRequests(response.data.requests || []);
      } else {
        setError(response.error?.message || 'Failed to load requests');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async (requestId) => {
    if (!window.confirm('Approve this dependent account request? This will create the dependent accounts immediately.')) {
      return;
    }

    const notes = prompt('Admin notes (optional):');
    
    setProcessing(requestId);
    setError('');
    try {
      const response = await dependentRequestsAPI.approve(requestId, notes || '');
      if (response.success) {
        alert(`Request approved! Created ${response.data.createdDependents.length} dependent account(s).`);
        loadRequests();
      } else {
        setError(response.error?.message || 'Failed to approve request');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Why are you rejecting this request? (This will be shown to the user)');
    if (!reason) return;

    setProcessing(requestId);
    setError('');
    try {
      const response = await dependentRequestsAPI.reject(requestId, reason);
      if (response.success) {
        alert('Request rejected.');
        loadRequests();
      } else {
        setError(response.error?.message || 'Failed to reject request');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="admin-page">
      <div style={{ marginBottom: '2rem' }}>
        <Link to="/admin" style={{ color: '#2196F3', textDecoration: 'none' }}>
          ← Back to Admin Dashboard
        </Link>
      </div>

      <h2>Dependent Account Requests</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Review and approve requests for dependent accounts from users who want family members to participate.
      </p>

      {error && (
        <div className="error-message" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="statusFilter" style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>
          Filter:
        </label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.5rem',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '1rem'
          }}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No {statusFilter !== 'all' ? statusFilter : ''} requests found.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {requests.map((request) => (
            <div
              key={request.requestId}
              className="card"
              style={{
                borderLeft: `4px solid ${
                  request.status === 'approved' ? '#4caf50' :
                  request.status === 'rejected' ? '#f44336' :
                  '#ff9800'
                }`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0' }}>
                    Request from {request.parentUsername}
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                    <strong>Email:</strong> {request.parentEmail}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem', color: '#666' }}>
                    <strong>Submitted:</strong> {new Date(request.createdAt).toLocaleString()}
                  </p>
                </div>
                <span
                  style={{
                    padding: '0.35rem 1rem',
                    borderRadius: '16px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    background:
                      request.status === 'approved' ? '#4caf50' :
                      request.status === 'rejected' ? '#f44336' :
                      '#ff9800',
                    color: 'white'
                  }}
                >
                  {request.status.toUpperCase()}
                </span>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong style={{ fontSize: '1rem' }}>Requested Dependents ({request.requestedDependents.length}):</strong>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {request.requestedDependents.map((dep, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '0.75rem',
                        background: '#f9f9f9',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <strong>{dep.displayName}</strong>
                      {dep.age && (
                        <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                          (Age: {dep.age})
                        </span>
                      )}
                      {dep.notes && (
                        <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                          {dep.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {request.reason && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Reason:</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.95rem', fontStyle: 'italic', color: '#555' }}>
                    "{request.reason}"
                  </p>
                </div>
              )}

              {request.status === 'pending' && (
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button
                    onClick={() => handleApprove(request.requestId)}
                    disabled={processing === request.requestId}
                    style={{
                      background: '#4caf50',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {processing === request.requestId ? 'Processing...' : '✓ Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(request.requestId)}
                    disabled={processing === request.requestId}
                    style={{
                      background: '#f44336',
                      color: 'white',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {processing === request.requestId ? 'Processing...' : '✕ Reject'}
                  </button>
                </div>
              )}

              {request.status !== 'pending' && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '4px' }}>
                  <strong>Review Details:</strong>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                    <strong>Reviewed by:</strong> {request.reviewedBy || 'N/A'}
                  </p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                    <strong>Reviewed at:</strong> {request.reviewedAt ? new Date(request.reviewedAt).toLocaleString() : 'N/A'}
                  </p>
                  {request.reviewNotes && (
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      <strong>Notes:</strong> {request.reviewNotes}
                    </p>
                  )}
                  {request.status === 'approved' && request.createdDependentIds && request.createdDependentIds.length > 0 && (
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                      <strong>Created {request.createdDependentIds.length} dependent account(s)</strong>
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DependentRequests;
